import { useCallback, useMemo, useState } from "react";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import {
  AccountData,
  NotificationType,
  TeamMember,
} from "@markit/common.types";
import RectangleButton from "../../Buttons/RectangleButton";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../Containers/PopupModalContainer";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import TeamMemberItem from "../../DisplayItem/TeamMemberItem";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import TextHoverButton from "../../Buttons/TextHoverButton";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { useDispatch } from "react-redux";
import ManageTeamMemberDropdown from "./ManageTeamMemberDropdown";
import DropdownArrowIcon from "../../DropdownsAndTabs/DropdownArrowIcon";

type TeamMemberDetailsModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  item: AccountData;
  teamMember: TeamMember;
};

const TeamMemberDetailsModal = (props: TeamMemberDetailsModalProps) => {
  const { isVisible, setIsVisible, item, teamMember } = props;
  const dispatch = useDispatch();
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const shareUrl = useMemo(
    () => `Email: ${teamMember.email}\nPassword: ${teamMember.password}`,
    [teamMember.email, teamMember.password]
  );

  const copyInfoOnPress = useCallback(() => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        showNotificationBanner(
          dispatch,
          "Copied!",
          NotificationType.AFFIRMATIVE
        );
      })
      .catch((err) => {
        alert(err);
      });
  }, [dispatch, shareUrl]);

  return isVisible ? (
    <PopupModalContainer
      headerComp={
        <div className="ColumnNormal" style={{ gap: 7 }}>
          <span className="sectionTitle">Team Member Details</span>
          <span className="smallBodySubtext" style={{ fontWeight: 400 }}>
            Share login info with the person you want to join your team. Manage
            team accounts anytime on the team page.
          </span>
        </div>
      }
      valueComp={
        <div className="ColumnNormal" style={{ gap: 20, paddingBottom: 20 }}>
          <TeamMemberItem
            item={item}
            teamMember={teamMember}
            onItemPress={() => {}}
            disabled
          />
          {item.haveNotFinishedInitialStartup ? (
            <div className="ColumnNormal" style={{ gap: 10 }}>
              <StandardBorderedContainer
                containerStyles={{
                  padding: 14,
                  backgroundColor: Colors.WHITE1,
                }}
              >
                <div className="ColumnNormal" style={{ gap: 10 }}>
                  <div className="AlignedRowSpaced">
                    <span className="bodyMedium">Email</span>
                    <span className="smallBodySubtext">{teamMember.email}</span>
                  </div>
                  <div className="AlignedRowSpaced">
                    <span className="bodyMedium">Password</span>
                    <span className="smallBodySubtext">
                      {teamMember.password}
                    </span>
                  </div>
                </div>
              </StandardBorderedContainer>
              <div className="RowNormal">
                <TextHoverButton
                  text="Copy Info"
                  onPress={copyInfoOnPress}
                  icon={
                    <Icon
                      icon={"ion:copy-outline"}
                      height={16}
                      style={{ color: Colors.BLUE5 }}
                    />
                  }
                  textStyles={{
                    color: Colors.BLUE5,
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      }
      fixedBottomContent={
        <div className="ColumnNormal" style={{ gap: 10 }}>
          <HorizontalDivider />
          <div
            className="AlignedRow"
            style={{ gap: 10, paddingInline: 14, flex: 1 }}
          >
            <div style={{ flex: 1 }}>
              <ManageTeamMemberDropdown
                teamMember={teamMember}
                isOpenDropdown={isOpenDropdown}
                setIsOpenDropdown={setIsOpenDropdown}
              >
                <RectangleButton
                  buttonLabel={"Manage"}
                  iconRight={
                    <DropdownArrowIcon
                      isOpen={isOpenDropdown}
                      color={Colors.BLACK}
                      height={14}
                    />
                  }
                  onPress={() => {}}
                  altColor={Colors.WHITE1}
                  altTextColor={Colors.BLACK}
                  altPaddingHorz={12}
                  altPaddingVert={14}
                  altBorderRadius={12}
                  containerStyles={{
                    border: `0.5px solid ${Colors.GRAY2}`,
                  }}
                />
              </ManageTeamMemberDropdown>
            </div>
          </div>
        </div>
      }
      noExit
      closeOnOutsidePress={!isOpenDropdown}
      closeModal={() => setIsVisible(false)}
      modalContentWidth={PopupModalContainerWidth.SMALL}
    />
  ) : null;
};

export default TeamMemberDetailsModal;
